import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, Store, MapPin, Star, IndianRupee, BadgeCheck, ChevronLeft, ShoppingBag, Ruler, Palette } from "lucide-react";

function priceLabel(p) {
  if (p?.price_range) return p.price_range;
  if (typeof p?.price_value === "number") return `₹${p.price_value.toLocaleString("en-IN")}`;
  return null;
}

export default function ProductDetail() {
  const { productKey } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const cart = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/detail/${encodeURIComponent(productKey)}`)
      .then((r) => setP(r.data))
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [productKey]);

  const add = async () => {
    if (!p) return;
    try {
      for (let i = 0; i < qty; i++) {
        await cart.add(p.vendor.vendor_id, p.name, 1, p.price_range || (p.price_value ? `₹${p.price_value}` : ""));
      }
      toast.success(`Added ${qty} × ${p.name} to cart`);
    } catch {
      toast.error("Could not add to cart");
    }
  };

  if (loading) return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-10">
      <Card className="p-10 text-center text-muted-foreground" data-testid="pd-loading">Loading…</Card>
    </div>
  );
  if (!p) return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-10">
      <Card className="p-10 text-center" data-testid="pd-notfound"><p className="text-sm text-muted-foreground">This item is no longer available.</p></Card>
    </div>
  );

  const price = priceLabel(p);
  // Build gallery: dedupe images, up to 4 shots. Fallback to legacy single image.
  const gallery = (p.images && p.images.length ? p.images : [p.image]).filter(Boolean).slice(0, 4);
  const attributeEntries = p.attributes && typeof p.attributes === "object"
    ? Object.entries(p.attributes).filter(([k, v]) => k && v)
    : [];

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-6" data-testid="pd-root">
      <Link to="/items" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground mb-3" data-testid="pd-back">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to items
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gallery */}
        <div className="space-y-3" data-testid="pd-gallery">
          <div className="aspect-square w-full bg-muted rounded-2xl overflow-hidden">
            {gallery.length > 0 ? (
              <img src={gallery[activeImg] || gallery[0]} alt={p.name} className="w-full h-full object-cover" data-testid="pd-hero-image" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ShoppingBag className="h-14 w-14" /></div>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2" data-testid="pd-thumbs">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square rounded-xl overflow-hidden bg-muted transition-all ${activeImg === i ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100"}`}
                  data-testid={`pd-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info + CTA */}
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" /> Item
            </div>
            <h1 className="font-heading text-3xl tracking-tighter mt-1" data-testid="pd-name">{p.name}</h1>
            <div className="text-sm text-muted-foreground inline-flex items-center gap-2 mt-2 flex-wrap" data-testid="pd-vendor">
              <Store className="h-4 w-4 text-purple-600" />
              Sold by <Link to={`/vendors/${p.vendor.vendor_id}`} className="font-medium text-foreground hover:underline">{p.vendor.business_name}</Link>
              {p.vendor.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
              <span className="inline-flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" /> {p.vendor.city}</span>
              {typeof p.vendor.rating === "number" && <span className="inline-flex items-center gap-1 text-xs"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {p.vendor.rating.toFixed(1)}</span>}
            </div>
          </div>

          <div className="flex items-baseline gap-3">
            {price ? (
              <div className="font-heading text-3xl inline-flex items-center" data-testid="pd-price">
                <IndianRupee className="h-6 w-6" />
                {price.replace("₹", "")}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Contact vendor for price</div>
            )}
            {p.is_veg === true && <Badge variant="outline" className="rounded-full border-emerald-500 text-emerald-700">Pure Veg</Badge>}
          </div>

          {/* Description */}
          {p.description && (
            <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap" data-testid="pd-desc">
              {p.description}
            </div>
          )}

          {/* Tags — clicking any tag runs a search for it */}
          {Array.isArray(p.tags) && p.tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="pd-tags">
              {p.tags.map((tag) => {
                const clean = tag.replace(/^#/, "");
                return (
                  <Link
                    key={tag}
                    to={`/items?q=${encodeURIComponent(clean)}`}
                    className="text-xs px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 transition-colors"
                    data-testid={`pd-tag-${clean}`}
                  >
                    #{clean}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Attributes */}
          {(p.size || p.color || attributeEntries.length > 0) && (
            <Card className="p-4 border-border/60" data-testid="pd-attributes">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Details</div>
              <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
                {p.size && (
                  <>
                    <dt className="text-muted-foreground inline-flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> Size</dt>
                    <dd className="font-medium" data-testid="pd-attr-size">{p.size}</dd>
                  </>
                )}
                {p.color && (
                  <>
                    <dt className="text-muted-foreground inline-flex items-center gap-1"><Palette className="h-3.5 w-3.5" /> Colour</dt>
                    <dd className="font-medium" data-testid="pd-attr-color">{p.color}</dd>
                  </>
                )}
                {attributeEntries.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</dt>
                    <dd className="font-medium">{String(v)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </Card>
          )}

          {/* Cart CTA */}
          <div className="flex items-center gap-2 pt-2">
            <div className="inline-flex items-center rounded-full border border-border/60 overflow-hidden" data-testid="pd-qty">
              <button type="button" className="h-10 w-10 hover:bg-muted" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pd-qty-dec">−</button>
              <span className="min-w-[36px] text-center font-medium" data-testid="pd-qty-value">{qty}</span>
              <button type="button" className="h-10 w-10 hover:bg-muted" onClick={() => setQty((q) => Math.min(20, q + 1))} data-testid="pd-qty-inc">+</button>
            </div>
            <Button
              size="lg"
              className="rounded-full flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              onClick={add}
              data-testid="pd-add-to-cart"
            >
              <ShoppingCart className="h-4 w-4 mr-2" /> Add to cart
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="pd-vendor-btn">
              <Link to={`/vendors/${p.vendor.vendor_id}`}>Vendor profile</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
